import React, { useState, useEffect, useRef,forwardRef,useImperativeHandle } from "react";
import { message, Select } from "antd";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import Speak from "assets/images/chat/speak.png";
import Speaking from "assets/images/chat/speaking.png";
import "./index.scss";
import { languageOptions, SILENCE_TIMEOUT } from "utils/constants";

const MicrophoneComponent = forwardRef(({ onTranscriptChange, onAutoSubmit, disabled = false }, ref) => {
    const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'granted', 'denied', 'prompt'
    const [showPermissionError, setShowPermissionError] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('en-US');

    const silenceTimeoutRef = useRef(null);
    const lastTranscriptRef = useRef('');
    const isFirstSpeechRef = useRef(true);

    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    // Check microphone permission status
    const checkMicrophonePermission = async () => {
        try {
            if (navigator.permissions) {
                const permission = await navigator.permissions.query({ name: 'microphone' });
                setPermissionStatus(permission.state);

                // Listen for permission changes
                permission.onchange = () => {
                    setPermissionStatus(permission.state);
                    if (permission.state === 'granted') {
                        setShowPermissionError(false);
                    }
                };
            } else {
                // Fallback for browsers that don't support permissions API
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stream.getTracks().forEach(track => track.stop());
                    setPermissionStatus('granted');
                } catch (error) {
                    setPermissionStatus('denied');
                }
            }
        } catch (error) {
            console.error('Error checking microphone permission:', error);
            setPermissionStatus('denied');
        }
    };

    // Request microphone permission
    const requestMicrophonePermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => track.stop());
            setPermissionStatus('granted');
            setShowPermissionError(false);
            return true;
        } catch (error) {
            console.error('Microphone permission denied:', error);
            setPermissionStatus('denied');
            setShowPermissionError(true);
            return false;
        }
    };

    // Open browser settings based on browser type
    const openBrowserSettings = () => {
        const userAgent = navigator.userAgent.toLowerCase();

        if (userAgent.includes('chrome')) {
            window.open('chrome://settings/content/microphone', '_blank');
        } else if (userAgent.includes('firefox')) {
            message.warning('Please go to Firefox Settings > Privacy & Security > Permissions > Microphone to enable microphone access for this site.');
        } else if (userAgent.includes('safari')) {
            message.warning('Please go to Safari > Preferences > Websites > Microphone to enable microphone access for this site.');
        } else if (userAgent.includes('edge')) {
            window.open('edge://settings/content/microphone', '_blank');
        } else {
            message.warning('Please check your browser settings to enable microphone access for this site.');
        }
    };

    // Initialize permission check
    useEffect(() => {
        if (browserSupportsSpeechRecognition) {
            checkMicrophonePermission();
        }
    }, [browserSupportsSpeechRecognition]);

    useEffect(() => {
        if (listening) {
            const currentTranscript = transcript.trim();

            // Update parent component with current transcript
            onTranscriptChange?.(currentTranscript);

            // Check if new speech was detected
            if (currentTranscript !== lastTranscriptRef.current) {
                lastTranscriptRef.current = currentTranscript;
                isFirstSpeechRef.current = false;

                // Reset the silence timeout since new speech was detected
                setSilenceTimeout();
            } else if (!isFirstSpeechRef.current) {
                // If no new speech and we've had speech before, maintain the timeout
                // The timeout was already set when speech was last detected
            } else {
                // This is the initial state - start the timeout for initial silence
                setSilenceTimeout();
            }
        }
    }, [transcript, listening, onTranscriptChange]);

    useEffect(() => {
        if (!listening) {
            clearSilenceTimeout();
        }

        return () => {
            clearSilenceTimeout();
        };
    }, [listening]);

    const handleStartListening = async () => {
        if (!browserSupportsSpeechRecognition) {
            message.error("Your browser doesn't support speech recognition.");
            return;
        }

        if (permissionStatus === 'denied') {
            message.error("Microphone access is blocked. Please enable it in your browser settings.To enable microphone access, go to Chrome settings → Privacy and Security → Site Settings → Microphone.");
            openBrowserSettings();
            return;
        }

        if (permissionStatus !== 'granted') {
            const granted = await requestMicrophonePermission();
            if (!granted) {
                return;
            }
        }

        try {
            SpeechRecognition.startListening({
                continuous: true,
                language: selectedLanguage
            });
        }
        catch (error) {
            console.error('Error starting speech recognition:', error);
            message.error('Failed to start speech recognition. Please try again.');
        }
    };

    const handleStopListening = () => {
        try {
            SpeechRecognition.stopListening();
            resetTranscript();
        } catch (error) {
            console.error('Error stopping speech recognition:', error);
        }
    };

    const clearSilenceTimeout = () => {
        if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }
    };

    const setSilenceTimeout = () => {
        clearSilenceTimeout();
        silenceTimeoutRef.current = setTimeout(() => {
            handleAutoStop();
        }, SILENCE_TIMEOUT);
    };

    const handleLanguageChange = (event) => {
        setSelectedLanguage(event);

        // If currently listening, restart with new language
        if (listening) {
            SpeechRecognition.stopListening();
            resetTranscript();
            setTimeout(() => {
                SpeechRecognition.startListening({
                    continuous: true,
                    language: event
                });
            }, 100);
        }

        const selectedLang = languageOptions.find(lang => lang.code === event);
        message.warning(`Language changed to ${selectedLang?.label}`);
    };

    const handleAutoStop = () => {
        try {
            SpeechRecognition.stopListening();
            const finalTranscript = transcript.trim();

            if (finalTranscript) {
                message.warning('It looks like you stopped speaking. Submitting automatically.');
                onAutoSubmit?.(finalTranscript);
            }
            else {
                message.warning('We didn’t catch any speech for a while. Transcription has been stopped automatically.');
            }

            // Reset for next session
            resetTranscript();
            isFirstSpeechRef.current = true;
            lastTranscriptRef.current = '';
        } catch (error) {
            console.error('Error during auto-stop:', error);
        }
    };

    useImperativeHandle(ref, () => ({
        stopListening: () => {
            try {
                SpeechRecognition.stopListening();
                // Clear any pending timeouts that might trigger handleAutoStop
                // If you have any setTimeout for auto-stop, clear them here
                resetTranscript();
                isFirstSpeechRef.current = true;
                lastTranscriptRef.current = '';
            } catch (error) {
                console.error('Error stopping listening:', error);
            }
        }
    }));

    // Don't render if browser doesn't support speech recognition
    if (!browserSupportsSpeechRecognition) {
        return null;
    }

    return (
        <div className="microphone-component">
            <Select
                value={selectedLanguage}
                onChange={handleLanguageChange}
                disabled={disabled || listening}
                size="small"
                variant="borderless"
                className="language-selector"
                style={{ minWidth: 120 }}
            >
                {languageOptions.map((option) => (
                    <Select.Option key={option.code} value={option.code}>
                        {option.label}
                    </Select.Option>
                ))}
            </Select>
            {!listening ? (
                <button
                    onClick={handleStartListening}
                    className="__micr"
                    disabled={disabled}
                    title={
                        permissionStatus === 'denied'
                            ? 'Microphone access blocked - click to open settings'
                            : 'Start voice input'
                    }
                >
                    <img src={Speak} alt="Start Listening" />
                </button>
            ) : (
                <button
                    onClick={handleStopListening}
                    className="__micr"
                    title="Stop voice input"
                >
                    <img src={Speaking} alt="Stop Listening" />
                </button>
            )}

            {/* Permission error indicator */}
            {showPermissionError && permissionStatus === 'denied' && (
                <div
                    className="permission-error"
                    onClick={openBrowserSettings}
                >
                    Click to enable microphone access
                </div>
            )}
        </div>
    );
})

export default MicrophoneComponent;